import { not, or } from 'tio-ui/utilities';
import ReCheckbox from 'tio-ui/components/checkbox';
import ReSwitch from 'tio-ui/components/switch';
import { PhInfo, PhX } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { isSectionAvailable } from '../../utils/filter-registry';
import ApplicationFilter from './filters/application.gts';
import Component from '@glimmer/component';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import PaymentFilter from './filters/payments.gts';
import ReSelect from 'tio-ui/components/select';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import { getTasApproversRequest } from 'tio-data/requests/company';
import { searchCompanyEmployees } from 'tio-common/utils/employee-search';
import { query, findRecord } from '@warp-drive/utilities/json-api';
import type IntlService from 'ember-intl/services/intl';
import type { Store } from '@warp-drive/core';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types';

interface AdvancedProgramFiltersSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      courseCashProgramsOnly?: boolean;
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;
      approverId?: string;
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;
      includeInvoiceDate?: boolean;
      showIndividualApplications?: boolean;
      onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
      monthOffset?: number;
      isAdminContext?: boolean;
      isCompanyLocked?: boolean;
      onCompanyChange?: (company?: CompanyModel) => void;
      /** Dataset field keys — used to hide filter sections irrelevant to the current dataset */
      availableFieldKeys?: string[];
    };
  Element: HTMLDivElement;
}

export class AdvancedProgramFilters extends Component<AdvancedProgramFiltersSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  get companyId() {
    return this.args.company?.id;
  }

  /** Missing field metadata shows every section for compatibility. */
  sectionVisible = (sectionKey: string) => {
    if (sectionKey === 'applications') {
      // Always show the toggle so users can switch into individual-application mode.
      return true;
    }
    const keys = this.args.availableFieldKeys;
    if (!keys || keys.length === 0) return true;
    return isSectionAvailable(sectionKey, keys);
  };

  // ── Program Template Data ──

  programsData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    if (!this.companyId) {
      return [];
    }
    const { content } = (await this.store.request(
      query<TasProgramTemplateModel>('tas-program-template', {
        'filter[company]': this.companyId,
      })
    )) as { content: { data: TasProgramTemplateModel[] } };
    return content.data;
  });

  get programOptions(): TasProgramTemplateModel[] {
    return this.programsData.value || [];
  }

  get programSelectOptions() {
    return this.programOptions.map((program: TasProgramTemplateModel) => ({
      label: program.programName,
      key: program.id,
    }));
  }

  // ── Partner Selection ──

  companiesData = trackedFunction(this, async (): Promise<CompanyModel[]> => {
    const { content } = (await this.store.request(
      query<CompanyModel>('company', {
        'filter[is-active]': 1,
        'page[limit]': 400,
      })
    )) as { content: { data: CompanyModel[] } };
    return content.data;
  });

  get companyItems(): CompanyModel[] {
    return this.companiesData.value || [];
  }

  companyDidChange = (key: string | null) => {
    const company = key ? this.companyItems.find((c) => c.id === key) : undefined;
    if (this.args.onCompanyChange) {
      this.args.onCompanyChange(company);
    }
  };

  // ── Program Template Filters ──

  programTemplateDidChange = (programTemplateId: string | null) => {
    this.args.onFilterUpdate({ programTemplateId });
  };

  programCreatedAtStartDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtStart: dateString });
  };

  programCreatedAtEndDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ programCreatedAtEnd: dateString });
  };

  // ── Course Filters ──

  includeCoursesDidChange = (state: boolean) => {
    this.args.onFilterUpdate({
      includeCourses: state,
      coursesBeginDate: '',
      coursesEndDate: '',
    });
  };

  coursesBeginDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesBeginDate: dateString });
  };

  coursesEndDateDidChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const dateString = date ? date.toISOString().split('T')[0] : '';
    this.args.onFilterUpdate({ coursesEndDate: dateString });
  };

  // ── Employee Filters ──

  @tracked employeeSearchTerm = '';

  employeesData = trackedFunction(this, async () => {
    if (!this.companyId || !this.employeeSearchTerm) return [];
    const employees = await searchCompanyEmployees(
      this.store,
      this.employeeSearchTerm,
      this.companyId
    );
    return employees.map((employee) => ({ key: employee.id, label: employee.fullName }));
  });

  selectedEmployeeRecord = trackedFunction(this, async () => {
    const id = this.args.employeeId;
    if (!id) return null;
    try {
      const { content } = (await this.store.request(findRecord<EmployeeModel>('employee', id))) as {
        content: { data: EmployeeModel };
      };
      return content.data;
    } catch {
      return null;
    }
  });

  get employeeItems() {
    const searchResults = this.employeesData.value || [];
    const selected = this.selectedEmployeeRecord.value;
    if (selected?.id && !searchResults.find((r) => r.key === selected.id)) {
      return [{ key: selected.id, label: selected.fullName }, ...searchResults];
    }
    return searchResults;
  }

  onEmployeeSearch = (query: string) => {
    this.employeeSearchTerm = query;
  };

  selectedEmployeeDidChange = (id: string | null) => {
    this.args.onFilterUpdate({ employeeId: id });
  };

  terminatedEmployeesDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ terminatedEmployees: state });
  };

  excludeTermVerifiedDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ excludeTermVerified: state });
  };

  // ── Approver Filters ──

  @tracked approver?: EmployeeModel;

  get approverOptions() {
    return (
      this.approversData.value?.map((e) => ({
        label: e.fullName,
        key: e.id,
      })) || []
    );
  }

  approversData = trackedFunction(this, async () => {
    if (!this.companyId) {
      return [];
    }
    const response = await this.store.request(getTasApproversRequest(this.companyId));
    const payload = response.content as Parameters<typeof this.store.push>[0];
    // Push the normalized document to obtain live identity-mapped records.
    return this.store.push(payload) as unknown as EmployeeModel[];
  });

  selectedApproverDidChange = (id: string | null) => {
    this.args.onFilterUpdate({ approverId: id });
  };

  // ── Invoice Filters ──

  includeInvoiceDateDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ includeInvoiceDate: state });
  };

  // ── Program Filters ──

  excludeNonVoucherProgramsDidChange = (value: boolean) => {
    this.args.onFilterUpdate({
      excludeNonVoucherPrograms: value,
    });
  };

  courseCashProgramsOnlyDidChange = (value: boolean) => {
    this.args.onFilterUpdate({
      courseCashProgramsOnly: value,
    });
  };

  <template>
    <div class="tio-col-flush gap-y-10 text-sm" ...attributes>
      {{#if @isAdminContext}}
        <fieldset>
          <div class="tio-col-extra-loose">
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Partner</h3>
              <div class="tio-bg-info-soft border tio-border-info rounded-lg p-4">
                <div class="tio-row-top-tight">
                  <PhInfo @size="20" class="tio-fg-info shrink-0 mt-0.5" />
                  <div>
                    <h4 class="tio-text-sm-semibold tio-fg-info-h mb-1">
                      Testing Context
                    </h4>
                    <p class="text-xs tio-fg-info-body">
                      Select a partner to preview their data and access partner-specific filters.
                      This selection is NOT saved with the report—it's for testing only.
                    </p>
                  </div>
                </div>
              </div>
              <ReSelect
                @label="Filter by Partner"
                @items={{this.companyItems}}
                @selected={{@company.id}}
                @onChange={{this.companyDidChange}}
                @searchable={{true}}
                @placeholder="All Partners"
                @size="sm"
                @isLoading={{this.companiesData.isLoading}}
                @disabled={{@isCompanyLocked}}
              />
              {{#if @isCompanyLocked}}
                <p class="text-xs tio-fg-muted italic mt-1">
                  Unpublish to change company
                </p>
              {{/if}}
            </div>
          </div>
        </fieldset>
      {{/if}}

      <fieldset>
        <div class="tio-col-extra-loose">
          {{#if (this.sectionVisible "programs")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Programs</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <ReSelect
                  @clearable={{true}}
                  @items={{this.programSelectOptions}}
                  @selected={{@programTemplateId}}
                  @label={{t "common.reporting.filter_by_program"}}
                  @isLoading={{this.programsData.isLoading}}
                  @onChange={{this.programTemplateDidChange}}
                  @size="sm"
                />
                <div class="tio-col-tight">
                  <label class="tio-text-sm-medium tio-fg-primary">Program Creation Date</label>
                  <div class="tio-row-tight">
                    <span class="text-sm tio-fg-secondary">From</span>
                    <div class="flex-1 relative">
                      {{! @glint-expect-error: return to this, follow up with dev}}
                      <EmberFlatpickr
                        @date={{or @programCreatedAtStart ""}}
                        @onChange={{this.programCreatedAtStartDidChange}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        placeholder="mm/dd/yyyy"
                        class="tio-form-input"
                      />
                      {{#if @programCreatedAtStart}}
                        <button
                          type="button"
                          {{on "click" (fn this.programCreatedAtStartDidChange (array))}}
                          class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-muted hover:tio-fg-secondary"
                          aria-label="Clear start date"
                        >
                          <PhX @size="16" />
                        </button>
                      {{/if}}
                    </div>
                    <span class="text-sm tio-fg-secondary">To</span>
                    <div class="flex-1 relative">
                      {{! @glint-expect-error: return to this, follow up with dev}}
                      <EmberFlatpickr
                        @date={{or @programCreatedAtEnd ""}}
                        @onChange={{this.programCreatedAtEndDidChange}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        placeholder="mm/dd/yyyy"
                        @minDate={{or @programCreatedAtStart ""}}
                        class="tio-form-input"
                      />
                      {{#if @programCreatedAtEnd}}
                        <button
                          type="button"
                          {{on "click" (fn this.programCreatedAtEndDidChange (array))}}
                          class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-muted hover:tio-fg-secondary"
                          aria-label="Clear end date"
                        >
                          <PhX @size="16" />
                        </button>
                      {{/if}}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          {{/if}}

          {{#if (this.sectionVisible "applications")}}
            <div class="tio-col">
              <h3
                class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted"
              >Applications</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <ApplicationFilter
                  @showIndividualApplications={{@showIndividualApplications}}
                  @applicationStates={{@applicationStates}}
                  @applicationCreatedAtStart={{@applicationCreatedAtStart}}
                  @applicationCreatedAtEnd={{@applicationCreatedAtEnd}}
                  @applicationDateRangePreset={{@applicationDateRangePreset}}
                  @onFilterUpdate={{@onFilterUpdate}}
                />
              </div>
            </div>
          {{/if}}

          {{#if (this.sectionVisible "payments")}}
            {{#if @showIndividualApplications}}
              <div class="tio-col">
                <div class="tio-col pb-3 border-b tio-border-default">
                  <PaymentFilter
                    @paymentYear={{@paymentYear}}
                    @monthOffset={{@monthOffset}}
                    @includePayments={{@includePayments}}
                    @paymentStates={{@paymentStates}}
                    @onFilterUpdate={{@onFilterUpdate}}
                  />
                </div>
              </div>
            {{/if}}
          {{/if}}

          {{#if (this.sectionVisible "invoices")}}
            <div class="tio-col pb-3 border-b tio-border-default">
              <ReCheckbox
                @label="Include Invoice Dates"
                @checked={{or @includeInvoiceDate false}}
                @onChange={{this.includeInvoiceDateDidChange}}
                @size="sm"
              />
            </div>
          {{/if}}

          {{#if (this.sectionVisible "courses")}}
            <div class="tio-col pb-3 border-b tio-border-default">
              <ReSwitch
                @label="Include Course Details"
                @checked={{or @includeCourses false}}
                @onChange={{this.includeCoursesDidChange}}
                @size="sm"
              />
              {{#if @includeCourses}}
                <div class="tio-col-tight ml-3">
                  <div class="tio-row-tight">
                    <span class="text-sm tio-fg-secondary">From</span>
                    <div class="flex-1 relative">
                      {{! @glint-expect-error: return to this, follow up with dev}}
                      <EmberFlatpickr
                        @date={{or @coursesBeginDate ""}}
                        @onChange={{this.coursesBeginDateDidChange}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        placeholder="mm/dd/yyyy"
                        class="tio-form-input"
                      />
                      {{#if @coursesBeginDate}}
                        <button
                          type="button"
                          {{on "click" (fn this.coursesBeginDateDidChange (array))}}
                          class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-muted hover:tio-fg-secondary"
                          aria-label="Clear begin date"
                        >
                          <PhX @size="16" />
                        </button>
                      {{/if}}
                    </div>
                    <span class="text-sm tio-fg-secondary">To</span>
                    <div class="flex-1 relative">
                      {{! @glint-expect-error: return to this, follow up with dev}}
                      <EmberFlatpickr
                        @date={{or @coursesEndDate ""}}
                        @onChange={{this.coursesEndDateDidChange}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        @minDate={{or @coursesBeginDate ""}}
                        placeholder="mm/dd/yyyy"
                        class="tio-form-input"
                      />
                      {{#if @coursesEndDate}}
                        <button
                          type="button"
                          {{on "click" (fn this.coursesEndDateDidChange (array))}}
                          class="absolute right-2 top-1/2 -translate-y-1/2 tio-fg-muted hover:tio-fg-secondary"
                          aria-label="Clear end date"
                        >
                          <PhX @size="16" />
                        </button>
                      {{/if}}
                    </div>
                  </div>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if (this.sectionVisible "approvers")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Approvers</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <ReSelect
                  @label={{t "common.reporting.filter_by_approver"}}
                  @items={{this.approverOptions}}
                  @selected={{@approverId}}
                  @onChange={{this.selectedApproverDidChange}}
                  @clearable={{true}}
                  @disabled={{not @showIndividualApplications}}
                  @isLoading={{this.approversData.isLoading}}
                  @size="sm"
                />
              </div>
            </div>
          {{/if}}

          {{#if (this.sectionVisible "employees")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Employees</h3>
              <div class="tio-col border-b tio-border-default pb-3">
                {{#if this.companyId}}
                  <ReSelect
                    @label={{t "common.reporting.filter_by_employee"}}
                    @items={{this.employeeItems}}
                    @selected={{@employeeId}}
                    @onChange={{this.selectedEmployeeDidChange}}
                    @searchable={{true}}
                    @onSearch={{this.onEmployeeSearch}}
                    @isLoading={{this.employeesData.isLoading}}
                    @clearable={{true}}
                    @disabled={{not @showIndividualApplications}}
                    @size="sm"
                    @placeholder="Search employees…"
                    @noResultsText="Type to search employees"
                  />
                {{/if}}
                <ReSwitch
                  @label={{t "common.reporting.terminated_employees"}}
                  @checked={{@terminatedEmployees}}
                  @onChange={{this.terminatedEmployeesDidChange}}
                  @size="sm"
                />
                <ReSwitch
                  @label={{t "common.reporting.exclude_term_verified"}}
                  @checked={{@excludeTermVerified}}
                  @onChange={{this.excludeTermVerifiedDidChange}}
                  @size="sm"
                />
              </div>
            </div>
          {{/if}}

          {{! EVIDENCE SECTION - Stub for future backend support }}
          {{#if (this.sectionVisible "evidence")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Evidence</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <p class="text-xs tio-fg-muted">
                  Evidence fields are available as columns. Filter support coming soon.
                </p>
              </div>
            </div>
          {{/if}}

          {{! ELIGIBILITY SECTION - Stub for future backend support }}
          {{#if (this.sectionVisible "eligibility")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Eligibility</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <p class="text-xs tio-fg-muted">
                  Eligibility fields are available as columns. Filter support coming soon.
                </p>
              </div>
            </div>
          {{/if}}

          {{! PARTICIPANT SECTION - Stub for future backend support }}
          {{#if (this.sectionVisible "participant")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Participant</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <p class="text-xs tio-fg-muted">
                  Participant fields are available as columns. Filter support coming soon.
                </p>
              </div>
            </div>
          {{/if}}

          {{! WITHDRAWAL SECTION - Stub for future backend support }}
          {{#if (this.sectionVisible "withdrawal")}}
            <div class="tio-col">
              <h3 class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">Withdrawal</h3>
              <div class="tio-col pb-3 border-b tio-border-default">
                <p class="text-xs tio-fg-muted">
                  Withdrawal fields are available as columns. Filter support coming soon.
                </p>
              </div>
            </div>
          {{/if}}

          {{#if @excludeNonVoucherPrograms}}
            <div class="tio-col">
              <ReCheckbox
                @label="Voucher Programs Only"
                @checked={{or @excludeNonVoucherPrograms false}}
                @onChange={{this.excludeNonVoucherProgramsDidChange}}
                @size="sm"
              />
            </div>
          {{/if}}
          {{#if (this.sectionVisible "programs")}}
            <div class="tio-col">
              <ReSwitch
                @label="Course Cash Programs Only"
                @checked={{or @courseCashProgramsOnly false}}
                @onChange={{this.courseCashProgramsOnlyDidChange}}
                @size="sm"
              />
            </div>
          {{/if}}
        </div>
      </fieldset>
    </div>
  </template>
}
